import { apiPaths } from "../../api-paths.js";
import { delay, HttpResponse, http } from "msw";
import { DateTime } from "luxon";
import { dateTimeToDateOnly } from "@holisto-web/date-utils";
import type {
  Checkout_GetCheckout_GetCheckoutQueryResponse,
  Checkout_GetCheckout_GetCheckoutQueryRoomNotAvailableResponse,
  Checkout_Prefill_CheckoutPrefillData,
  Checkout_V1_Requests_CreateCheckoutPrefillTokenRequest,
  Checkout_V1_Requests_GetCheckoutPrefillDataRequest,
} from "../../swagger-types/v1/index.js";
import { replaceCurrencyDeep } from "@test-utils/tools";
import { cloneDeep } from "lodash-es";

export const checkoutHandlersFactory = {
  "200": (
    responseTransformer?: (
      response: Checkout_GetCheckout_GetCheckoutQueryResponse,
    ) => Promise<Checkout_GetCheckout_GetCheckoutQueryResponse>,
  ) => {
    return [
      http.get(`**${apiPaths.checkout.get}`, async () => {
        const body = responseTransformer
          ? await responseTransformer(cloneDeep(getCheckout200Response.default))
          : getCheckout200Response.default;
        return HttpResponse.json(body, { status: 200 });
      }),
    ];
  },
  400: () => {
    return [
      http.get(`**${apiPaths.checkout.get}`, async () => {
        return HttpResponse.json(getCheckout400Response.invalidCheckoutId, { status: 400 });
      }),
    ];
  },
  404: () => {
    return [
      http.get(`**${apiPaths.checkout.get}`, async () => {
        return HttpResponse.json({}, { status: 404 });
      }),
    ];
  },
  networkError: () => {
    return [
      http.get(`**${apiPaths.checkout.get}`, async () => {
        return HttpResponse.error();
      }),
    ];
  },
  422: (
    responseTransformer?: (
      response: Checkout_GetCheckout_GetCheckoutQueryRoomNotAvailableResponse,
    ) => Promise<Checkout_GetCheckout_GetCheckoutQueryRoomNotAvailableResponse>,
  ) => {
    return [
      http.get(`**${apiPaths.checkout.get}`, async () => {
        const defaultBody: Checkout_GetCheckout_GetCheckoutQueryRoomNotAvailableResponse = {
          searchCriteria: {
            checkIn: "2030-01-01",
            checkOut: "2030-01-01",
            occupancies: [{ adults: 2, children: [] }],
          },
        };
        const body = responseTransformer ? await responseTransformer(defaultBody) : defaultBody;
        return HttpResponse.json(body, { status: 422 });
      }),
    ];
  },
};

export const checkoutHandlers = {
  "200": checkoutHandlersFactory["200"](),
  "200MalformedStripeMetadata": checkoutHandlersFactory["200"](async (response) => {
    response.checkoutPaymentProvider.providerMetadata = "";
    return response;
  }),
  "200BrazilianCurrency": checkoutHandlersFactory["200"](async (response) => {
    const withReplacedCurrency = replaceCurrencyDeep(response, "BRL");
    withReplacedCurrency.checkoutPaymentProvider.isTaxIdRequired = true;
    return withReplacedCurrency;
  }),
  400: checkoutHandlersFactory["400"](),
  404: checkoutHandlersFactory["404"](),
  422: checkoutHandlersFactory["422"](),
  networkError: checkoutHandlersFactory["networkError"](),
} as const;

export const verificationHandler200 = [
  http.post(`**${apiPaths.checkout.verifyCredits}`, async () => {
    const body = {
      amount: {
        amount: 50,
        currencyCode: "USD",
      },
    };
    return HttpResponse.json(body, { status: 200 });
  }),
];
export const verificationHandler200highAmount = [
  http.post(`**${apiPaths.checkout.verifyCredits}`, async () => {
    const body = {
      amount: {
        amount: 5000,
        currencyCode: "USD",
      },
    };
    return HttpResponse.json(body, { status: 200 });
  }),
];

export const creditValidationHandlers = {
  "200": verificationHandler200,
  "200highAmount": verificationHandler200highAmount,
};

export const checkoutPrefillHandlersFactory = {
  createToken200: ({
    delayMs,
    onRequest,
  }: {
    delayMs?: number;
    onRequest?: (data: Checkout_V1_Requests_CreateCheckoutPrefillTokenRequest, headers: Headers) => void;
  } = {}) => {
    return [
      http.post(`**${apiPaths.checkout.createPrefillToken}`, async ({ request }) => {
        if (delayMs) {
          await delay(delayMs);
        }
        onRequest?.((await request.json()) as Checkout_V1_Requests_CreateCheckoutPrefillTokenRequest, request.headers);
        return HttpResponse.json({ prefillToken: "secure-prefill-token" }, { status: 200 });
      }),
    ];
  },
  createToken500: () => {
    return [
      http.post(`**${apiPaths.checkout.createPrefillToken}`, () => {
        return HttpResponse.json({}, { status: 500 });
      }),
    ];
  },
  getData200: (
    data: Checkout_Prefill_CheckoutPrefillData,
    onRequest?: (data: Checkout_V1_Requests_GetCheckoutPrefillDataRequest) => void,
  ) => {
    return [
      http.post(`**${apiPaths.checkout.getPrefillData}`, async ({ request }) => {
        onRequest?.((await request.json()) as Checkout_V1_Requests_GetCheckoutPrefillDataRequest);
        return HttpResponse.json(data, { status: 200 });
      }),
    ];
  },
  getData404: () => {
    return [
      http.post(`**${apiPaths.checkout.getPrefillData}`, () => {
        return HttpResponse.json({}, { status: 404 });
      }),
    ];
  },
  getData500: () => {
    return [
      http.post(`**${apiPaths.checkout.getPrefillData}`, () => {
        return HttpResponse.json({}, { status: 500 });
      }),
    ];
  },
};

export const insuranceQuoteHandlers = {
  "200": [
    http.get(`**${apiPaths.checkout.getInsuranceQuote}`, async () => {
      const body = {
        quote: {
          quoteKey: "6e0835cc-49a6-4399-95a0-ff89891a62c3",
          price: {
            amount: 59.6,
            currency: "USD",
          },
          paymentPrice: {
            amount: 59.6,
            currency: "USD",
          },
          policyTermsUrl: "https://refundable.me/extended/en",
          disclaimer:
            "Upgrade your ticket and receive a 100% refund if you cannot attend and can provide evidence related to one of the many reasons in our Terms & Conditions, which you accept when you select a Refundable Booking.",
          insuranceProvider: "ProtectGroup",
        },
      };
      return HttpResponse.json(body, { status: 200 });
    }),
  ],
};

export type ICheckoutHandlers = typeof checkoutHandlers;

const checkIn = DateTime.now();
const checkOut = checkIn.plus({ day: 1 });

const getCheckout200Response: { default: Checkout_GetCheckout_GetCheckoutQueryResponse } = {
  default: {
    deal: {
      checkoutId: "b791a35e-df99-4c49-acd7-092a81f004af",
      roomName: "Standard Room",
      roomCode: "2b6abefc-2fde-433f-a140-9ff08d9f2876",
      boardCode: "AllInclusive",
      boardDescription: "All Inclusive",
      checkIn: dateTimeToDateOnly(checkIn),
      checkOut: dateTimeToDateOnly(checkOut),
      cancellationPolicy: {
        type: "NonRefundable",
        deadline: null,
        description:
          "This room is non-refundable and cannot be changed. No-shows and early checkouts are not eligible for a refund.",
      },
      pricing: {
        fees: {
          total: 123.04,
          currencyCode: "USD",
          items: [
            {
              code: "CityTax",
              amount: 7.0,
              currencyCode: "USD",
              description: "government tax",
            },
            {
              code: "CityTax",
              amount: 91.8,
              currencyCode: "USD",
              description: "Destination fee",
            },
            {
              code: "CityTax",
              amount: 24.24,
              currencyCode: "USD",
              description: "City tax",
            },
          ],
        },
        taxes: {
          total: 16.05,
          currencyCode: "USD",
          items: [
            {
              code: "Vat",
              amount: 16.05,
              currencyCode: "USD",
              description: "Tax",
            },
          ],
        },
        hotelFees: {
          total: 0,
          currencyCode: "USD",
          items: [],
        },
        hotelFeeTaxes: {
          total: 0,
          currencyCode: "USD",
          items: [],
        },
        currency: "USD",
        nightlyPrice: 206,
        totalRoomPrice: 272.92,
        totalRoomsPrice: 272.92,
        totalPriceWithHotelFees: 412.01,
      },
      paymentPricing: {
        fees: {
          total: 123.04,
          currencyCode: "USD",
          items: [
            {
              code: "CityTax",
              amount: 7.0,
              currencyCode: "USD",
              description: "government tax",
            },
            {
              code: "CityTax",
              amount: 91.8,
              currencyCode: "USD",
              description: "Destination fee",
            },
            {
              code: "CityTax",
              amount: 24.24,
              currencyCode: "USD",
              description: "City tax",
            },
          ],
        },
        taxes: {
          total: 16.05,
          currencyCode: "USD",
          items: [
            {
              code: "Vat",
              amount: 16.05,
              currencyCode: "USD",
              description: "Tax",
            },
          ],
        },
        hotelFees: {
          total: 0,
          currencyCode: "USD",
          items: [],
        },
        hotelFeeTaxes: {
          total: 0,
          currencyCode: "USD",
          items: [],
        },
        currency: "USD",
        nightlyPrice: 206,
        totalRoomPrice: 272.92,
        totalRoomsPrice: 272.92,
        totalPriceWithHotelFees: 412.01,
      },
    },
    occupancies: [
      {
        adults: 2,
        children: [],
      },
    ],
    ancillaries: [
      {
        category: "Tip",
        type: "Tip",
        prices: [
          {
            price: {
              amount: 4.12,
              currency: "USD",
            },
            percentage: 1,
          },
          {
            price: {
              amount: 8.24,
              currency: "USD",
            },
            percentage: 2,
          },
          {
            price: {
              amount: 20.6,
              currency: "USD",
            },
            percentage: 5,
          },
        ],
        paymentPrices: [
          {
            price: {
              amount: 4.12,
              currency: "USD",
            },
            percentage: 1,
          },
          {
            price: {
              amount: 8.24,
              currency: "USD",
            },
            percentage: 2,
          },
          {
            price: {
              amount: 20.6,
              currency: "USD",
            },
            percentage: 5,
          },
        ],
      },
      {
        category: "Package",
        type: "VipService",
        prices: [
          {
            price: {
              amount: 4.99,
              currency: "USD",
            },
            percentage: null,
          },
        ],
        paymentPrices: [
          {
            price: {
              amount: 4.99,
              currency: "USD",
            },
            percentage: null,
          },
        ],
      },
    ],
    checkoutPaymentProvider: {
      isPaymentInDifferentCurrency: false,
      isTaxIdRequired: false,
      providerCode: "Stripe",
      providerMetadata:
        '{"PublicKey":"pk_test_51PxCceBFjgT4ftd9kR8JsnMsskfxUBiTSGFXlj9QMJXpBEigrJGmdUnm7hosqkzWzlps4mKvxtZ32EBhqP98MuM000O9rIhhAX","CardTypeMapping":{"amex":"AmericanExpress","visa":"Visa","mastercard":"MasterCard","diners":"Diners","jcb":"JCB","discover":"Discover","unionpay":"UnionPay","unknown":"Unknown"}}',
    },
  },
};

const getCheckout400Response = {
  invalidCheckoutId: {
    type: "https://tools.ietf.org/html/rfc7231#section-6.5.1",
    title: "One or more validation errors occurred.",
    status: 400,
    errors: {
      checkoutId: ["The value '272basde2ca-fabb-4446-8699-2b2300251d24' is not valid."],
    },
    traceId: "00-72cbe078697a6ebb93eec72a99cdd12f-ed303b9e28707ef8-00",
  },
};
